import QRCode from 'qrcode';
import type { ImageDataLike } from './types';

export interface GeneratedQr extends ImageDataLike {
  moduleCount: number;
  modulePixels: number;
  quietZoneModules: number;
}

/**
 * Deterministically rasterize a QR code straight from its module matrix
 * (via the `qrcode` package's encoder) into an RGBA pixel buffer, with no
 * canvas, no anti-aliasing, and no intermediate SVG/PNG round trip.
 *
 * Every module is either pure black (0,0,0,255) or pure white
 * (255,255,255,255) — required by the "white background; pure black QR"
 * display spec, and it also makes the generated image the best possible
 * input for the integrity round-trip decode (no blur from resampling).
 *
 * This function is pure and has no DOM/Node dependency, so it runs
 * identically in the browser (subject QR card, import integrity check) and
 * in Node (unit tests, the real-sample verification script).
 */
export function generateQrImage(
  payload: string,
  opts: { modulePixels?: number; quietZoneModules?: number; errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H' } = {}
): GeneratedQr {
  const modulePixels = opts.modulePixels ?? 8;
  const quietZoneModules = opts.quietZoneModules ?? 4;
  const errorCorrectionLevel = opts.errorCorrectionLevel ?? 'M';

  const qr = QRCode.create(payload, { errorCorrectionLevel });
  const moduleCount = qr.modules.size;
  const moduleData = qr.modules.data; // Uint8Array, bit0 set => dark module

  const totalModules = moduleCount + quietZoneModules * 2;
  const pixelSize = totalModules * modulePixels;

  const data = new Uint8ClampedArray(pixelSize * pixelSize * 4);
  // Fill white.
  data.fill(255);

  for (let my = 0; my < moduleCount; my++) {
    for (let mx = 0; mx < moduleCount; mx++) {
      const dark = (moduleData[my * moduleCount + mx] & 1) === 1;
      if (!dark) continue;
      const px0 = (mx + quietZoneModules) * modulePixels;
      const py0 = (my + quietZoneModules) * modulePixels;
      for (let py = py0; py < py0 + modulePixels; py++) {
        const rowStart = (py * pixelSize + px0) * 4;
        for (let px = 0; px < modulePixels; px++) {
          const i = rowStart + px * 4;
          data[i] = 0;
          data[i + 1] = 0;
          data[i + 2] = 0;
          data[i + 3] = 255;
        }
      }
    }
  }

  return { data, width: pixelSize, height: pixelSize, moduleCount, modulePixels, quietZoneModules };
}
